/**
 * Utility functions for handling targeted account restrictions and operations
 */

/**
 * Validates and filters redirect account restrictions
 * @param {Array} redirectAccountRestrictions - Array of account restrictions
 * @returns {Array|null} - Validated restrictions or null if invalid
 */
export const redirectAccountRestrictionsValidator = (redirectAccountRestrictions) => {
    if (!Array.isArray(redirectAccountRestrictions)) {
        return null;
    }

    const validListofAccounts = redirectAccountRestrictions.filter((item) => {
        if (item == null) return false;

        const requiredKeys = ['fipId', 'fiType', 'accType', 'maskedAccNumber'];

        // Check required keys (excluding restrictions)
        for (let key of requiredKeys) {
            if (!(key in item) || item[key] == null) return false;
        }

        for (let key of ['fipId', 'fiType', 'accType', 'maskedAccNumber']) {
            const value = item[key];
            if (typeof value !== 'string' || value.trim() === '') return false;
        }

        // Check restrictions field - allow null or valid object
        if ('restrictions' in item) {
            const restrictions = item.restrictions;
            
            // Allow null restrictions
            if (restrictions === null) return true;
            
            // If restrictions exist, validate them
            if (typeof restrictions !== 'object') return false;

            if (restrictions.isMandatory !== undefined) {
                if (typeof restrictions.isMandatory !== 'string') return false;
                
                const normalized = restrictions.isMandatory.trim().toLowerCase();
                if (normalized !== 'true' && normalized !== 'false') return false;
            }
        }

        return true;
    });

    return validListofAccounts.length > 0 ? validListofAccounts : null;
};

/**
 * Filters linked accounts based on redirect account restrictions
 * @param {Array} linkedAccounts - Array of linked accounts
 * @returns {Array} - Filtered accounts that match restrictions
 */
export const filterLinkedAccountsByRestrictions = (linkedAccounts) => {
    if (!global.redirectAccountRestrictions) {
        return linkedAccounts;
    }

    const filteredAccounts = linkedAccounts.filter((account) => {
        const matches = global.redirectAccountRestrictions.some((restriction) => {
            const fipIdMatch = account.fipId === restriction.fipId;
            const fiTypeMatch = account.FIType === restriction.fiType;
            const accTypeMatch = account.accType === restriction.accType;
            // Compare last 4 digits: restriction might have just "6086" while account has "XXXXX6086"
            const maskedMatch = account.maskedAccNumber === restriction.maskedAccNumber || 
                               account.maskedAccNumber?.slice(-4) === restriction.maskedAccNumber;
            return fipIdMatch && fiTypeMatch && accTypeMatch && maskedMatch;
        });
        return matches;
    });
    return filteredAccounts;
};

/**
 * Checks if an account is mandatory based on redirect restrictions
 * @param {Object} account - Account object to check
 * @returns {boolean} - True if account is mandatory
 */
export const isAccountMandatory = (account) => {
    if (!global.redirectAccountRestrictions) {
        return false;
    }

    const restriction = global.redirectAccountRestrictions.find(
        (restriction) =>
            restriction.fipId === account.fipId &&
            restriction.fiType === account.FIType &&
            restriction.accType === account.accType &&
            restriction.maskedAccNumber === account.maskedAccNumber?.slice(-4)
    );

    return restriction?.restrictions?.isMandatory?.toLowerCase() === 'true';
};

/**
 * Checks if all mandatory accounts are linked
 * @param {Array} displayedAccounts - Array of discovered accounts by FIP
 * @param {Array} linkedAccounts - Array of linked accounts
 * @returns {boolean} - True if all mandatory accounts are linked
 */
export const isAllMandatoryAccountsLinked = (displayedAccounts, linkedAccounts) => {
    const allDiscoveredAccountsWithFip = [];
    displayedAccounts.forEach((fip) => {
        if (fip.discoveredAccounts) {
            // Add fipId to each discovered account for proper matching
            fip.discoveredAccounts.forEach(account => {
                allDiscoveredAccountsWithFip.push({ ...account, fipId: fip.fipId });
            });
        }
    });
    
    const mandatoryRestrictions = allDiscoveredAccountsWithFip.filter(
        (account) => account?.restrictions?.isMandatory?.toLowerCase() === "true"
    );
    
    const allMandatoryAccountsLinked = mandatoryRestrictions.every((restriction) =>
        linkedAccounts.some(
            (account) => {
                const fipIdMatch = account.fipId === restriction.fipId;
                const fiTypeMatch = account.FIType === restriction.FIType;
                const accTypeMatch = account.accType === restriction.accType;
                // Compare last 4 digits: restriction might have just "6086" while account has "XXXXX6086"
                const maskedMatch = account.maskedAccNumber === restriction.maskedAccNumber || 
                                   account.maskedAccNumber?.slice(-4) === restriction.maskedAccNumber;
                return fipIdMatch && fiTypeMatch && accTypeMatch && maskedMatch;
            }
        )
    );
    
    return allMandatoryAccountsLinked;
};

/**
 * Filters selected accounts based on redirect restrictions for consent processing
 * @param {Array} selectedAccounts - Array of selected accounts
 * @returns {Array} - Filtered accounts that match restrictions
 */
export const filterSelectedAccountsByRestrictions = (selectedAccounts) => {
    if (!global.redirectAccountRestrictions) {
        return selectedAccounts;
    }

    return selectedAccounts.filter((account) => {
        return global.redirectAccountRestrictions.some(
            (restriction) =>
                restriction.fipId === account.fipId &&
                restriction.fiType === account.FIType &&
                restriction.accType === account.accType &&
                restriction.maskedAccNumber === account.maskedAccNumber.slice(-4)
        );
    });
};

/**
 * Checks if all mandatory restrictions are satisfied by linked accounts
 * @param {Array} linkedAccounts - Array of linked accounts
 * @param {Array} matchedRestrictions - Only restrictions that were discovered (optional, defaults to global.redirectAccountRestrictions)
 * @returns {boolean} - True if all mandatory restrictions are satisfied
 */
export const areAllMandatoryRestrictionsSatisfied = (linkedAccounts, matchedRestrictions = null) => {
    const restrictionsToCheck = matchedRestrictions || global.redirectAccountRestrictions;
    
    if (!restrictionsToCheck || restrictionsToCheck.length === 0) {
        return true;
    }

    return restrictionsToCheck.every(
        (restriction) =>
            restriction.restrictions?.isMandatory?.toLowerCase() !== 'true' ||
            linkedAccounts.some(
                (account) => {
                    const fipIdMatch = account.fipId === restriction.fipId;
                    const fiTypeMatch = account.FIType === restriction.fiType;
                    const accTypeMatch = account.accType === restriction.accType;
                    // Compare last 4 digits: restriction might have just "6086" while account has "XXXXX6086"
                    const maskedMatch = account.maskedAccNumber === restriction.maskedAccNumber || 
                                       account.maskedAccNumber?.slice(-4) === restriction.maskedAccNumber;
                    return fipIdMatch && fiTypeMatch && accTypeMatch && maskedMatch;
                }
            )
    );
};

/**
 * Checks if account restrictions are satisfied by discovered accounts
 * @param {Array} discoveredAccounts - Array of discovered accounts
 * @param {Array} linkedAccounts - Array of linked accounts
 * @returns {boolean} - True if restrictions are satisfied
 */
export const areAccountRestrictionsSatisfied = (discoveredAccounts, linkedAccounts) => {
    if (!global.redirectAccountRestrictions) {
        return true;
    }

    return global.redirectAccountRestrictions.every((restriction) =>
        linkedAccounts.some(
            (account) => {
                const fipIdMatch = account.fipId === restriction.fipId;
                const fiTypeMatch = account.FIType === restriction.fiType;
                const accTypeMatch = account.accType === restriction.accType;
                // Compare last 4 digits: restriction might have just "6086" while account has "XXXXX6086"
                const maskedMatch = account.maskedAccNumber === restriction.maskedAccNumber || 
                                   account.maskedAccNumber?.slice(-4) === restriction.maskedAccNumber;
                return fipIdMatch && fiTypeMatch && accTypeMatch && maskedMatch;
            }
        )
    );
};

/**
 * Merges restrictions into matched accounts WITHOUT filtering
 * Shows ALL accounts but matched accounts get their restrictions
 * @param {Array} fullDiscoveredAccounts - Array of all discovered accounts by FIP
 * @param {Array} targetedAccounts - Array of targeted account restrictions
 * @returns {Array} - All accounts with restrictions merged for matched ones
 */
export const mergeRestrictionsIntoAccounts = (fullDiscoveredAccounts, targetedAccounts) => {
    // Return all accounts as-is if no targeted accounts specified
    if (!targetedAccounts || !Array.isArray(targetedAccounts) || targetedAccounts.length === 0) {
        return fullDiscoveredAccounts;
    }
    
    // Map through all discovered accounts and add restrictions to matched ones
    const result = fullDiscoveredAccounts.map((fip) => {
        const accountsWithRestrictions = fip.discoveredAccounts.map((account) => {
            const targetedAccount = targetedAccounts.find(
                (target) => {
                    const fipIdMatch = target.fipId === fip.fipId;
                    const fiTypeMatch = target.fiType === account.FIType;
                    const accTypeMatch = target.accType === account.accType;
                    // Compare last 4 digits: restriction might have just "6086" while account has "XXXXX6086"
                    const maskedMatch = target.maskedAccNumber === account.maskedAccNumber || 
                                       account.maskedAccNumber?.slice(-4) === target.maskedAccNumber;
                    return fipIdMatch && fiTypeMatch && accTypeMatch && maskedMatch;
                }
            );

            // If this account matches a targeted account, merge in its restrictions
            // Otherwise, return account as-is
            return targetedAccount
                ? { ...account, restrictions: targetedAccount.restrictions }
                : account;
        });
        
        return {
            ...fip,
            discoveredAccounts: accountsWithRestrictions,
        };
    });

    return result;
};

/**
 * Filters discovered accounts to only include targeted accounts
 * @param {Array} fullDiscoveredAccounts - Array of all discovered accounts by FIP
 * @param {Array} targetedAccounts - Array of targeted account restrictions
 * @returns {Array} - Filtered discovered accounts
 */
export const getTargetedAccounts = (fullDiscoveredAccounts, targetedAccounts) => {
    // Return all accounts if no targeted accounts specified
    if (!targetedAccounts || !Array.isArray(targetedAccounts) || targetedAccounts.length === 0) {
        return fullDiscoveredAccounts;
    }
    
    // Filter the fullDiscoveredAccounts based on the targeted accounts
    const result = fullDiscoveredAccounts
        .map((fip) => {
            // Filter the discovered accounts for the current FIP
            const filteredAccounts = fip.discoveredAccounts
                .filter((account) => {
                    // Check if this account matches any targeted account
                    return targetedAccounts.some((target) => {
                        const fipIdMatch = target.fipId === fip.fipId;
                        const fiTypeMatch = target.fiType === account.FIType;
                        const accTypeMatch = target.accType === account.accType;
                        // Compare last 4 digits: target might have just "6086" while account has "XXXXX6086"
                        const maskedMatch = target.maskedAccNumber === account.maskedAccNumber || 
                                           account.maskedAccNumber?.slice(-4) === target.maskedAccNumber;
                        return fipIdMatch && fiTypeMatch && accTypeMatch && maskedMatch;
                    });
                })
                .map((account) => {
                    const targetedAccount = targetedAccounts.find(
                        (target) => {
                            const fipIdMatch = target.fipId === fip.fipId;
                            const fiTypeMatch = target.fiType === account.FIType;
                            const accTypeMatch = target.accType === account.accType;
                            // Compare last 4 digits: target might have just "6086" while account has "XXXXX6086"
                            const maskedMatch = target.maskedAccNumber === account.maskedAccNumber || 
                                               account.maskedAccNumber?.slice(-4) === target.maskedAccNumber;
                            return fipIdMatch && fiTypeMatch && accTypeMatch && maskedMatch;
                        }
                    );

                    const mappedAccount = targetedAccount
                        ? { ...account, restrictions: targetedAccount.restrictions }
                        : account;
                    return mappedAccount;
                });
            
            // Return the FIP object with filtered accounts
            return {
                ...fip,
                discoveredAccounts: filteredAccounts,
            };
        })
        .filter((fip) => {
            // Include FIPs that have targeted accounts OR have allDiscoveredAccounts (for later swap)
            const hasTargetedAccounts = fip.discoveredAccounts.length > 0;
            const hasAllAccounts = fip.allDiscoveredAccounts && fip.allDiscoveredAccounts.length > 0;
            return hasTargetedAccounts || hasAllAccounts;
        });

    return result;
}; 